// Carriers whose tracking pages we can link to.
//
// Kept as a fixed list rather than free text so a recorded tracking number
// becomes a link the customer can actually follow. "other" is deliberate:
// the shop can still record a number from anyone, it just shows as plain
// text instead of a URL we'd be guessing at.
//
// Mirrors CARRIERS in backend/app/schemas.py, which builds the same links
// for the shipped-order email.
export const CARRIERS = [
  { value: 'ups', label: 'UPS' },
  { value: 'fedex', label: 'FedEx' },
  { value: 'usps', label: 'USPS' },
  { value: 'other', label: 'Other' },
] as const;

export type CarrierValue = (typeof CARRIERS)[number]['value'];

const TRACKING_URLS: Record<string, string> = {
  ups: 'https://www.ups.com/track?loc=en_US&tracknum={number}',
  fedex: 'https://www.fedex.com/fedextrack/?trknbr={number}',
  usps: 'https://tools.usps.com/go/TrackConfirmAction?tLabels={number}',
};

export const carrierLabel = (carrier?: string): string => {
  if (!carrier) return '';
  const known = CARRIERS.find(c => c.value === carrier.trim().toLowerCase());
  // An unrecognised value is shown as the shop typed it — orders predating
  // the dropdown carry free text.
  return known ? known.label : carrier;
};

/** The carrier's tracking page, or '' when we can't build one. */
export const trackingUrl = (carrier?: string, number?: string): string => {
  if (!carrier || !number) return '';
  const template = TRACKING_URLS[carrier.trim().toLowerCase()];
  if (!template) return '';
  // Carriers print tracking numbers in spaced groups and people paste them
  // that way; every one of these sites wants them closed up.
  const bare = number.replace(/\s+/g, '');
  return template.replace('{number}', encodeURIComponent(bare));
};

/**
 * Fold a stored carrier onto one of the known codes.
 *
 * Carrier was a free-text field before this list existed, so an older order
 * can hold anything at all. The backend now only accepts the codes, and a
 * value it rejects would fail the save with no obvious cause.
 */
export const normalizeCarrier = (carrier?: string): string => {
  const raw = (carrier ?? '').trim().toLowerCase();
  if (!raw) return '';
  const match = CARRIERS.find(c => c.value === raw || c.label.toLowerCase() === raw);
  return match ? match.value : 'other';
};
